'use client';

import { HeaderText, InternalLink, LuvImage } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import {
  TypeLink_internal,
  TypePlayKitsByAgeFields,
} from '@/core/generated-types';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import React, { useState } from 'react';

import {
  CTA_NAMES,
  PAGE_LOCATIONS,
  trackItemClickedEvent,
  CLICKED_ITEM_TYPES,
} from '@/utils/rudderstack';

interface ImageItemProps {
  playKitsByAge: TypePlayKitsByAgeFields['playKitsByAge'];
  playKit: TypeLink_internal;
  index: number;
  bgColor: string;
}

const imageBackgroundColors = [
  '#60CBC2',
  '#FEDB00',
  '#B85BBF',
  '#BBDC00',
  '#FF9955',
];

const ImageItem = ({
  playKit,
  playKitsByAge,
  index,
  bgColor,
}: ImageItemProps) => {
  const [isHovered, setIsHovered] = useState(false);
  if (!playKitsByAge) return null;

  const hasOneItemInLastRow =
    playKitsByAge.length - 1 === index && playKitsByAge.length % 2 !== 0;

  const hoverAnimation = {
    initial: { scale: 1 },
    hover: { scale: 1.06 },
  };

  return (
    <InternalLink
      link={playKit}
      classes={classNames(
        'flex flex-col text-center font-bold text-base col-span-1 lg:text-lg',
        {
          //  Center on mobile if last row has just 1 item
          'col-span-2 lg:col-span-1': hasOneItemInLastRow,
        },
      )}
    >
      <div
        className="w-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative mx-auto">
          <div
            className={classNames(
              'absolute top-[53%] left-[46%] -translate-x-1/2 -translate-y-1/2 w-[98px] lg:w-[120px] h-[98px] lg:h-[120px] rounded-full',
              {
                '!left-[48.5%] lg:!left-[46%]': hasOneItemInLastRow,
              },
            )}
            style={{
              backgroundColor: bgColor,
            }}
          />

          {/* Image and zoom effect */}
          <div className="rounded-full overflow-hidden w-[98px] lg:w-[120px] h-[98px] lg:h-[120px] relative z-10 mx-auto">
            <motion.div
              variants={hoverAnimation}
              initial="initial"
              animate={isHovered ? 'hover' : 'initial'}
              transition={{ duration: 0.3 }}
              className="w-full h-full flex justify-center items-center"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <LuvImage
                imageData={playKit.fields.linkImage}
                className="w-[98px] mx-auto lg:w-[120px]"
              />
            </motion.div>
          </div>
        </div>
        <p
          className={classNames('mt-2', {
            'text-blue-deep': isHovered,
          })}
        >
          {playKit.fields.entryName}
        </p>
      </div>
    </InternalLink>
  );
};

const PlayKitsByAge = ({
  playKitsByAge,
  headerText,
}: TypePlayKitsByAgeFields) => {
  return (
    <div className="flex flex-col bg-white">
      <HeaderText
        size={HeaderSize.H4}
        text={headerText}
        classes="mt-8 text-center !text-2xl lg:!text-[32px] lg:mt-16"
      />
      <div
        data-testid="age-based-pk"
        className="mx-auto px-[53px] mt-6 mb-8 flex flex-wrap items-center justify-center gap-x-16 gap-y-5 lg:gap-20 lg:mt-8 lg:mb-16"
      >
        {playKitsByAge?.map((playKit, index) => (
          <div
            key={playKit.fields.entryName}
            onClick={(e) => {
              e.preventDefault();
              trackItemClickedEvent({
                type: CLICKED_ITEM_TYPES.LINK,
                cta: playKit?.fields?.entryName as CTA_NAMES,
                pageLocation: PAGE_LOCATIONS.PLAY_KITS_AGE_BASED,
                url: window.location.href,
              });
            }}
          >
            <ImageItem
              bgColor={imageBackgroundColors[index]}
              {...{
                playKit,
                index,
                playKitsByAge,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlayKitsByAge;
